import { ProgressBar } from "@/components/downgrade-flow/progress-bar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGetActiveSubscriptionPlans } from "@/hooks/use-subscription";
import { ProtectedWrapper } from "@/providers/protected-wrapper";

import {
  PaymentInterval,
  SubscriptionStatus,
} from "@/types/subscription.types";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  Calendar,
  Check,
  Loader2,
  Mail,
  Trash,
  X,
} from "lucide-react";

import StatusScreen from "@/components/status-screen";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  B2BPlanType,
  B2CPlanType,
  PlanCategory,
} from "@/pages/billing/pricing-data";
import CancelConfirmation from "@/pages/cancel-plan/cancel-confirmation";
import CancelFeedback from "@/pages/cancel-plan/cancel-feedback";
import CancelOptimizationPlan from "@/pages/cancel-plan/cancel-optimization-plan";
import {
  CancelPlanProvider,
  useCancelPlanContext,
} from "@/pages/cancel-plan/cancel-provider";
import CancelReviewPlan from "@/pages/cancel-plan/cancel-review-plan";
import { format } from "date-fns";
import { z } from "zod";

const cancelPlanSearchSchema = z.object({
  planId: z.string({ required_error: "Plan ID is required" }).nonempty({
    message: "Plan ID is required",
  }),
  subscriptionId: z
    .string({ required_error: "Subscription ID is required" })
    .nonempty({ message: "Subscription ID is required" }),
  planType: z.nativeEnum(B2CPlanType).or(z.nativeEnum(B2BPlanType)),
  quantity: z
    .number()
    .int()
    .min(1, { message: "Quantity must be a positive integer" })
    .catch(1),
});

export type CancelPlanSearch = z.infer<typeof cancelPlanSearchSchema>;

export const Route = createFileRoute("/cancelPlan")({
  component: CancelPlanComponent,
  validateSearch: (search) => cancelPlanSearchSchema.parse(search),
  errorComponent: () => {
    return (
      <StatusScreen
        icon={<X />}
        iconWrapperClassName="text-red-500 bg-red-50 dark:bg-red-900/20 dark:text-red-400"
        title=" Missing Required Information"
        description="Please provide all required information to proceed with the cancellation. Please check the parameters and try again."
        variant="link-button"
        buttonText="Back to Dashboard"
        buttonLink="/dashboard"
      />
    );
  },
});

function CancelPlanComponent() {
  return (
    <ProtectedWrapper>
      <CancelPlanProvider>
        <CancelPlanContent />
      </CancelPlanProvider>
    </ProtectedWrapper>
  );
}

const cancelSteps = [
  { number: 1, label: "Review Plan" },
  { number: 2, label: "Optimization Options" },
  { number: 3, label: "Feedback" },
  { number: 4, label: "Confirm" },
];

function CancelPlanContent() {
  const {
    state,
    planDetails,
    subscription,
    handleStateChange,
    isCancellationPending,
    handleConfirm,
    searchParams,
  } = useCancelPlanContext();

  const { isLoading } = useGetActiveSubscriptionPlans();

  if (isLoading) {
    return (
      <main className="flex min-h-screen flex-col p-4 bg-gray-50 dark:bg-background items-center justify-center">
        <div className="max-w-3xl mx-auto w-full flex justify-center items-center">
          <p>Loading plan details...</p>
        </div>
      </main>
    );
  }

  if (!subscription || !planDetails) {
    return (
      <StatusScreen
        icon={<X />}
        iconWrapperClassName="text-red-500 bg-red-50 dark:bg-red-900/20 dark:text-red-400"
        title="Plan or Subscription Details Not Found"
        description="We couldn't find the plan and subscription details you were looking for."
        variant="link-button"
        buttonText="Back to Dashboard"
        buttonLink="/dashboard"
      />
    );
  }

  if (subscription.status === SubscriptionStatus.PENDING) {
    return (
      <StatusScreen
        icon={<X />}
        iconWrapperClassName="text-amber-500 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400"
        title="Cancellation Not Allowed"
        description="You cannot cancel this subscription because it is currently in a pending state. This usually means the payment is incomplete or the plan hasn't been activated yet. Please wait until the subscription becomes active before attempting to cancel."
        variant="link-button"
        buttonText="Back to Dashboard"
        buttonLink="/dashboard"
      />
    );
  }

  if (state.isSuccess) {
    return (
      <CancellationSuccessScreen
        expiryDate={format(subscription.endDate, "MMM d, yyyy")}
        planName={
          planDetails.planId?.planName || planDetails.addonPlanId?.name || ""
        }
        interval={subscription.interval}
        planType={planDetails.planId?.category}
        quantity={searchParams.quantity}
      />
    );
  }

  return (
    <main className="flex min-h-screen flex-col  bg-gray-50 dark:bg-background">
      <div className="max-w-5xl mx-auto w-full p-6">
        <Button asChild>
          <Link
            to="/dashboard"
            className="rounded-full text-sm flex items-center gap-2"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Dashboard</span>
          </Link>
        </Button>

        <div className="flex flex-col mt-3 ">
          <h1 className="page-heading">Cancel Subscription</h1>
          <p className="text-muted-foreground mb-8">
            We're sorry to see you go. Please complete the steps below to cancel
            your subscription.
          </p>
        </div>

        <ProgressBar currentStep={state.step} steps={cancelSteps} />

        {state.step === 1 && <CancelReviewPlan />}

        {state.step === 2 && <CancelOptimizationPlan />}

        {state.step === 3 && <CancelFeedback />}

        {state.step === 4 && <CancelConfirmation />}

        <Dialog
          open={state.showConfirmationDialog}
          onOpenChange={() =>
            handleStateChange({ showConfirmationDialog: false })
          }
        >
          <DialogContent
            onEscapeKeyDown={(e) => {
              if (isCancellationPending) {
                e.preventDefault();
              }
            }}
            onPointerDownOutside={(e) => {
              if (isCancellationPending) {
                e.preventDefault();
              }
            }}
            onInteractOutside={(e) => {
              if (isCancellationPending) {
                e.preventDefault();
              }
            }}
          >
            <DialogHeader>
              <DialogTitle>Are you sure you want to cancel?</DialogTitle>
              <DialogDescription>
                This action will cancel your{" "}
                {planDetails.planId?.planName || planDetails.addonPlanId?.name}{" "}
                plan effective at the end of your current billing cycle on{" "}
                {format(subscription.endDate, "MMM d, yyyy")}.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <p className="text-muted-foreground">
                You will continue to have access to all features until this
                date, after which your access will be terminated and no further
                charges will be applied.
              </p>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() =>
                  handleStateChange({ showConfirmationDialog: false })
                }
              >
                Go Back
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirm}
                disabled={isCancellationPending}
              >
                {isCancellationPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Cancelling...</span>
                  </>
                ) : (
                  "Yes, Cancel My Subscription"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </main>
  );
}

function CancellationSuccessScreen({
  expiryDate,
  planName,
  planType,
  interval,
  quantity,
}: {
  expiryDate: string;
  planName: string;
  planType?: PlanCategory;
  interval: PaymentInterval;
  quantity: number;
}) {
  const renderPlanName = () => {
    if (planType === undefined || planType === PlanCategory.CUSTOM) {
      return planName;
    }

    return (
      <>
        {planType} - {planName}
      </>
    );
  };

  return (
    <main className="flex min-h-screen flex-col p-4 md:p-24 bg-gray-50 items-center justify-center">
      <div className="max-w-3xl mx-auto w-full text-center">
        <div className="mb-6 flex justify-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <Check className="size-8 text-green-600" />
          </div>
        </div>

        <h1 className="text-3xl font-bold mb-4">Cancellation Confirmed</h1>
        <p className="text-gray-600 mb-8 max-w-lg mx-auto">
          Your {quantity} {renderPlanName()}{" "}
          {quantity > 1 ? "subscriptions" : "subscription"} has been cancelled.
          You'll maintain access until <strong>{expiryDate}</strong> (end of
          current {interval} billing cycle) with no further charges.
        </p>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>What Happens Next</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-left">
              <div className="flex gap-3">
                <div className="bg-blue-100 p-2 rounded-full h-min">
                  <Calendar className="size-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-medium">Continued Access</h3>
                  <p className="text-gray-600">
                    You'll continue to have full access to all features until{" "}
                    {expiryDate}.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="bg-blue-100 p-2 rounded-full h-min">
                  <Mail className="size-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-medium">Cancellation Email</h3>
                  <p className="text-gray-600">
                    You'll receive a confirmation email with details about your
                    cancellation.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="bg-blue-100 p-2 rounded-full h-min">
                  <Trash className="size-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-medium">Data Retention</h3>
                  <p className="text-gray-600">
                    Once plan is cancelled you will not be able to access your
                    plan.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-center">
          <Link to="/" replace>
            <Button>Return to Dashboard</Button>
          </Link>
        </div>
      </div>
    </main>
  );
}
